<template>
    <PanelFrame ref="panelFrame" panel-class="datatable-app" resize-directions="all" :content-class="contentClass">
        <template #titlebar-left>
            <button v-if="dt.state.config?.filterLayout === 'sidebar' && !isCompact" class="panel-titlebar-btn" @click.stop="dt.state.sidebarOpen = !dt.state.sidebarOpen">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="2" y="2" width="20" height="20" rx="2" ry="2"/><line x1="9" y1="2" x2="9" y2="22"/>
                </svg>
            </button>
            <span v-if="dt.state.config?.icon" class="dt-titlebar-icon" v-html="dt.state.config.icon"></span>
            <span class="dt-titlebar-title">{{ dt.state.config?.title || $t('common.dataTable.title') }}</span>
            <span v-if="dt.state.rows.length" class="dt-titlebar-count">{{ dt.state.rows.length }}</span>
        </template>

        <template #titlebar-actions>
            <button v-if="!isCompact" ref="colMenuBtn" class="panel-titlebar-btn" @click.stop="toggleColMenu" :title="$t('common.dataTable.columns')">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
            </button>
            <button v-if="dt.state.config?.exportable !== false" class="panel-titlebar-btn" @click.stop="dt.exportCsv()" :title="$t('common.dataTable.export')">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            </button>
        </template>

        <template #default>
            <div class="dt-body-wrap">
                <DataTableFilterSidebar v-if="showSidebar"
                    :filters="sidebarFilters"
                    :filter-values="dt.state.filterValues"
                    :width="sidebarWidth"
                    :is-compact="isCompact"
                    @filter="onFilter"
                    @clear="onClearFilters"
                    @divider-start="startSidebarResize"/>

                <div class="dt-main">
                    <DataTableToolbar v-if="toolbarFilters.length"
                        :filters="toolbarFilters"
                        :filter-values="dt.state.filterValues"
                        :is-compact="isCompact"
                        :loading="dt.state.loading"
                        @filter="onFilter"/>

                    <div ref="scroll" class="dt-scroll" :class="{'dt-scroll--has-selection': dt.state.selectedIds.size > 0, 'dt-scroll--has-pagination': dt.state.config?.pagination === 'paged'}">
                        <DataTableHeader
                            :columns="visibleCols"
                            :selectable="selectable"
                            :expandable="expandable"
                            :has-actions="hasActions"
                            :sort-key="dt.state.sortKey"
                            :sort-dir="dt.state.sortDir"
                            :all-selected="dt.allSelected.value"
                            :some-selected="dt.someSelected.value"
                            :column-widths="dt.state.columnWidths"
                            :is-compact="isCompact"
                            @sort="dt.toggleSort($event)"
                            @toggle-all="onToggleAll"
                            @resize="dt.resizeColumn"
                            @column-menu="onColumnMenu"/>

                        <DataTableBody
                            :rows="dt.state.rows"
                            :columns="visibleCols"
                            :row-key="dt.state.config?.rowKey || 'id'"
                            :selectable="selectable"
                            :expandable="expandable"
                            :has-actions="hasActions"
                            :actions="dt.state.config?.actions"
                            :action-style="dt.state.config?.actionStyle || 'dropdown'"
                            :selected-ids="dt.state.selectedIds"
                            :expanded-ids="dt.state.expandedIds"
                            :editing-cell="dt.state.editingCell"
                            :editable="dt.state.config?.editable || false"
                            :render-expand="dt.state.config?.renderExpand"
                            :loading="dt.state.loading"
                            :loading-more="dt.state.loadingMore"
                            :has-more="dt.state.hasMore"
                            :pagination="dt.state.config?.pagination || 'scroll'"
                            :loader="dt.state.config?.loader || 'skeleton'"
                            :column-widths="dt.state.columnWidths"
                            :is-compact="isCompact"
                            @select="dt.toggleSelect($event)"
                            @expand="dt.toggleExpand($event)"
                            @action="onRowAction"
                            @row-click="onRowClick"
                            @edit-start="dt.startEdit"
                            @edit-commit="dt.commitEdit"
                            @edit-cancel="dt.cancelEdit"
                            @load-more="dt.loadMore"/>
                    </div>

                    <DataTablePagination v-if="dt.state.config?.pagination === 'paged'"
                        :current-page="dt.state.page"
                        :page-limit="dt.state.config?.pageLimit || 25"
                        :total-count="dt.state.config?.totalCount"
                        :loading-more="dt.state.loadingMore"
                        @page-change="onPageChange"/>

                    <div v-if="dt.state.selectedIds.size > 0 && bulkActions.length" class="dt-selection-bar">
                        <span class="dt-selection-chip" @click="dt.clearSelection()">
                            {{ $t('common.dataTable.selectedCount', { count: dt.state.selectedIds.size }) }}
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        </span>
                        <button v-for="ba in bulkActions" :key="ba.key" class="dt-selection-btn" :class="{'dt-selection-btn--danger': ba.danger}" @click="onBulkAction(ba.key)">
                            <span v-if="ba.icon" v-html="ba.icon"></span>
                            <span class="dt-selection-btn-label">{{ ba.label }}</span>
                        </button>
                    </div>
                </div>
            </div>
            <div v-if="colMenuOpen" ref="colMenu" class="dt-col-menu" :style="colMenuStyle" @click.stop>
                <div class="dt-col-menu-header">{{ $t('common.dataTable.columns') }}</div>
                <div v-for="col in dt.state.columns" :key="col.key" class="dt-col-menu-item" @click="dt.toggleColumnVisibility(col.key)">
                    <svg v-if="dt.state.columnVisibility[col.key] !== false" class="dt-select-check" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                    <span v-else class="dt-select-check-space"></span>
                    <span>{{ col.label }}</span>
                </div>
            </div>
        </template>
    </PanelFrame>
</template>

<script>
import './datatable-panel.css'
import { createPanelMixin } from '@/components/panel/usePanelBase'
import PanelFrame from '@/components/panel/PanelFrame.vue'
import DataTableHeader from './DataTableHeader.vue'
import DataTableBody from './DataTableBody.vue'
import DataTableToolbar from './DataTableToolbar.vue'
import DataTableFilterSidebar from './DataTableFilterSidebar.vue'
import DataTablePagination from './DataTablePagination.vue'
import dataTableService from './dataTableService'

const dataTablePanelMixin = createPanelMixin({
    panelId: 'dataTablePanel',
    minWidth: 480,
    minHeight: 400,
    defaults: {
        floatingMode: false,
        width: 1000,
        height: 620,
        sidebarWidth: 260,
    },
    hasInnerLayout: true,
    minSidebar: 180,
    maxSidebar: 320,
    settingsItems: ['alwaysOnTop', 'divider', 'transparent', 'divider', 'resetWindowSize', 'resetInnerLayout'],
    onEscape(vm) {
        if (dataTableService.state.editingCell) {
            dataTableService.cancelEdit()
            return true
        }
        return false
    },
    onClose() {
        dataTableService._fireCallback('onClose')
        dataTableService.state.isOpen = false
        dataTableService.state.config = null
    },
    onOpen(vm) {
        if (vm.$refs.scroll) vm.$refs.scroll.scrollTop = 0
    },
})

export default {
    name: 'DataTablePanel',
    mixins: [dataTablePanelMixin],
    components: { PanelFrame, DataTableHeader, DataTableBody, DataTableToolbar, DataTableFilterSidebar, DataTablePagination },
    data() {
        return {
            colMenuOpen: false,
            colMenuStyle: null,
        }
    },
    computed: {
        dt() {
            return dataTableService
        },
        contentClass() {
            let cls = ''
            if (this.panelTransparent) cls += ' panel-content--transparent'
            return cls
        },
        selectable() {
            return this.dt.state.config?.selectable || false
        },
        expandable() {
            return this.dt.state.config?.expandable || false
        },
        hasActions() {
            return (this.dt.state.config?.actions || []).length > 0
        },
        bulkActions() {
            return this.dt.state.config?.bulkActions || []
        },
        tableAreaWidth() {
            const pw = this.windowWidth || 1000
            const sw = this.showSidebar ? (this.sidebarWidth || 220) : 0
            return pw - sw
        },
        visibleCols() {
            let cols = this.dt.visibleColumns.value || []
            if (this.isCompact) cols = cols.filter(c => !c.compactHide)
            const w = this.tableAreaWidth
            if (w < 500) {
                cols = cols.filter(c => c.priority === 'essential')
            } else if (w < 750) {
                cols = cols.filter(c => c.priority !== 'optional')
            }
            return cols
        },
        showSidebar() {
            if (this.isCompact) return false
            return this.dt.state.config?.filterLayout === 'sidebar' && this.dt.state.sidebarOpen
        },
        sidebarFilters() {
            return this.dt.state.config?.filters || []
        },
        toolbarFilters() {
            if (this.dt.state.config?.filterLayout === 'sidebar' && !this.isCompact) {
                return (this.dt.state.config?.filters || []).filter(f => f.type === 'text')
            }
            return this.dt.state.config?.filters || []
        },
    },
    methods: {
        onResetInnerLayout() {
            this.sidebarWidth = 260
            this.panelSaveLayoutDebounced()
            dataTableService.state.columnWidths = {}
        },
        onFilter(key, value) {
            dataTableService.setFilter(key, value)
        },
        onClearFilters() {
            dataTableService.clearFilters()
        },
        onToggleAll() {
            if (dataTableService.allSelected.value) dataTableService.deselectAll()
            else dataTableService.selectAll()
        },
        onRowAction(key, row) {
            dataTableService._fireCallback('onRowAction', key, row)
        },
        onRowClick(row) {
            dataTableService._fireCallback('onRowClick', row)
        },
        onBulkAction(key) {
            dataTableService._fireCallback('onBulkAction', key, dataTableService.getSelectedRows())
        },
        onPageChange(page) {
            dataTableService.goToPage(page)
        },
        _getContainingBlockRect() {
            const el = this.$el?.closest('.panel-content--transparent')
            if (el) return el.getBoundingClientRect()
            return { top: 0, left: 0, right: window.innerWidth, bottom: window.innerHeight }
        },
        toggleColMenu() {
            if (this.colMenuOpen) { this.closeColMenu(); return }
            const btn = this.$refs.colMenuBtn
            if (!btn) return
            const rect = btn.getBoundingClientRect()
            const cr = this._getContainingBlockRect()
            this.colMenuStyle = {
                position: 'fixed',
                top: (rect.bottom + 4 - cr.top) + 'px',
                right: (cr.right - rect.right) + 'px',
                zIndex: 9999,
            }
            this.colMenuOpen = true
            this._addColMenuClose()
        },
        onColumnMenu({ x, y }) {
            if (this.colMenuOpen) this.closeColMenu()
            const cr = this._getContainingBlockRect()
            this.colMenuStyle = {
                position: 'fixed',
                top: (y - cr.top) + 'px',
                left: (x - cr.left) + 'px',
                zIndex: 9999,
            }
            this.colMenuOpen = true
            this._addColMenuClose()
        },
        closeColMenu() {
            this.colMenuOpen = false
            this.colMenuStyle = null
            if (this._colMenuCloseFn) {
                document.removeEventListener('mousedown', this._colMenuCloseFn)
                this._colMenuCloseFn = null
            }
        },
        _addColMenuClose() {
            if (this._colMenuCloseFn) document.removeEventListener('mousedown', this._colMenuCloseFn)
            const handler = (e) => {
                const menu = this.$refs.colMenu
                if (menu && menu.contains(e.target)) return
                const btn = this.$refs.colMenuBtn
                if (btn && btn.contains(e.target)) return
                this.closeColMenu()
            }
            this._colMenuCloseFn = handler
            setTimeout(() => document.addEventListener('mousedown', handler), 0)
        },
    },
    beforeUnmount() {
        this.closeColMenu()
    },
    watch: {
        'dt.state.isOpen'(val) {
            if (val && !this.visible) this.open()
            else if (!val && this.visible) this.close()
        },
    },
}
</script>
